// One author (FS-075's profile, redrawn after FS-392): a room in their tone with the plate, name,
// dates and traditions set on it; the registry's reviewed life in the serif; then their works
// standing on a shelf (FS-390). Works whose text the web may show open here; the rest are
// references, dimmed, "In the Bible Standard app" (no price, lock or badge: free/paid D5). For the
// Fathers, whose writings sit inside the Ante-Nicene volumes, their own sections are listed by volume.
import { createFileRoute, Link } from "@tanstack/react-router";
import type { CSSProperties } from "react";
import { AuthorPlate } from "../../../components/AuthorPlate";
import { LinkButton } from "../../../components/Button";
import { EmptyState } from "../../../components/Empty";
import { Icon } from "../../../components/Icon";
import { bookWidth, Ledge, objectKind, ShelfBook, ShelfSlot, shelfStyles as sh } from "../../../components/library/Shelf";
import { SectionHeader } from "../../../components/SectionHeader";
import { Skeleton } from "../../../components/Skeleton";
import ui from "../../../components/ui.module.css";
import { usePlans } from "../../../data/planQueries";
import { availability, AVAILABILITY_LINE, useAuthors, useCatalogue, useToc, type Author, type Work } from "../../../data/works";
import { morphFrom, morphName } from "../../../motion/shared";
import { toneOr } from "../../../lib/tone";
import { useTitle } from "../../../lib/useTitle";
import s from "./authors.module.css";

export const Route = createFileRoute("/_app/authors/$id")({ component: AuthorPage });

const ERA_FACETS = new Set(["Fathers", "Medieval", "Reformers", "Puritans", "Modern", "Ante-Nicene", "Post-Nicene"]);
const STAND = 156;

function AuthorPage() {
  const { id } = Route.useParams();
  const { data: authors, isPending } = useAuthors();
  const { data: catalogue } = useCatalogue();
  const { data: plans = [] } = usePlans();
  const a = authors?.find((x) => x.id === id);
  useTitle(a?.name ?? "Author");

  if (isPending)
    return (
      <div className={ui.page} aria-busy="true">
        <Skeleton shape="block" height={240} />
      </div>
    );
  if (!a)
    return (
      <div className={ui.page}>
        <EmptyState title="This author isn’t in the library." body="They may be listed under another name.">
          <LinkButton to="/authors">All authors</LinkButton>
        </EmptyState>
      </div>
    );

  const works = a.works.map((w) => ({ ...w, work: catalogue?.works.find((x) => x.id === w.libraryId) })).filter((w): w is typeof w & { work: Work } => !!w.work);
  const here = works.filter((w) => w.work.text).length;
  const reading = plans.filter((p) => p.companionWork && a.works.some((w) => w.libraryId === p.companionWork!.libraryId));
  const writings = works.filter((w) => w.sections && w.work.text);
  const facets = a.facets.filter((f) => !ERA_FACETS.has(f));

  return (
    <div className={ui.page}>
      <div className={ui.pageHead}>
        <Link to="/authors" className={ui.back} aria-label="All authors">
          <Icon name="back" />
        </Link>
      </div>
      <header className={s.hero} style={{ ["--plate-tone" as string]: toneOr(a.tone, a.name) } as CSSProperties}>
        <span className={s.heroPlate} style={{ viewTransitionName: morphName("author", a.id) }}>
          <AuthorPlate name={a.name} dates={null} tone={a.tone} size={112} />
        </span>
        <div className={s.heroWords}>
          <h1 className={s.heroName}>{a.name}</h1>
          {a.dates && <p className={s.heroDates}>{a.dates.replace(/\s*-\s*/g, "–")}</p>}
          {facets.length > 0 && (
            <div className={s.facets}>
              {facets.map((f) => (
                <Link key={f} to="/authors" search={{ tradition: f }} className={s.facet}>
                  {f}
                </Link>
              ))}
            </div>
          )}
        </div>
      </header>
      {a.bio && <p className={s.bio}>{a.bio}</p>}

      {works.length === 0 ? (
        <p className={s.band}>Not yet in the library.</p>
      ) : (
        <section className={s.section} aria-labelledby="shelf-head">
          <SectionHeader
            id="shelf-head"
            title="Works"
            className={s.shelfHead}
            description={`${works.length} ${works.length === 1 ? "work" : "works"} in the library${here ? `, ${here} to read here` : ""}.`}
          />
          <Ledge height={STAND} labelledBy="shelf-head">
            {works.map((w) => (
              <li key={w.libraryId}>
                <ShelfWork work={w.work} author={a} />
              </li>
            ))}
          </Ledge>
        </section>
      )}

      {writings.length > 0 && (
        <section className={s.section} aria-labelledby="writings-head">
          <SectionHeader id="writings-head" title="Their writings" description="Inside the volumes above, under their own name." />
          <div className={s.writings}>
            {writings.map((w) => (
              <div key={w.libraryId}>
                <p className={s.volume}>{w.work.title}</p>
                <TheirSections libraryId={w.libraryId} uids={w.sections!} />
              </div>
            ))}
          </div>
        </section>
      )}

      {reading.length > 0 && (
        <section className={s.section} aria-labelledby="plans-head">
          <SectionHeader id="plans-head" title={`Plans that read ${a.name.split(" ")[0]}`} />
          <ul className={s.plans}>
            {reading.map((p) => (
              <li key={p.id}>
                <Link to="/plans/about/$planId" params={{ planId: p.id }}>
                  {p.title}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function ShelfWork({ work, author }: { work: Work; author: Author }) {
  const how = availability(work);
  return (
    <Link to="/books/$id" params={{ id: work.id }} className={sh.slot} onClick={morphFrom("book", work.id)}>
      <ShelfSlot
        width={bookWidth(STAND, objectKind(work.kind))}
        book={<ShelfBook id={work.id} title={work.title} shortTitle={work.shortTitle} author={work.author} tone={author.tone} kind={work.kind} height={STAND} dimmed={how !== "web"} />}
        caption={
          <>
            <span className={sh.title}>{work.title}</span>
            {work.written && <span className={sh.meta}>{work.written}</span>}
            <span className={how === "web" ? sh.state : sh.stateQuiet}>{AVAILABILITY_LINE[how]}</span>
          </>
        }
      />
    </Link>
  );
}

/** A Father's own writings inside a volume: the sections with their name on them, at their top level. */
function TheirSections({ libraryId, uids }: { libraryId: string; uids: string[] }) {
  const { data: toc } = useToc(libraryId);
  if (!toc) return null;
  const mine = new Set(uids);
  const rows = toc.rows.flatMap((r, i) => (r[0] && mine.has(r[0]) && !(r[3] >= 0 && mine.has(toc.rows[r[3]]![0] ?? "")) ? [{ uid: r[0], title: r[1], i }] : []));
  return (
    <ul className={s.sections} aria-label="Their writings in this volume">
      {rows.map((r) => (
        <li key={r.uid}>
          <Link to="/books/$id/$section" params={{ id: libraryId, section: r.uid }}>
            {r.title}
          </Link>
        </li>
      ))}
    </ul>
  );
}
